import { tokyoYear, toWareki } from "../datetime.ts";
import type { SensitivityRules } from "../review-due.ts";
import { COMMON_RULES } from "./common.ts";
import type { ProfessionPack } from "./types.ts";

const fiscalLabel = (y: number) => `${y}年度（${toWareki(y).replace(/年$/, "年度")}）`;

/** What in a labour-and-insurance article changes with the year: rates and amounts, benefits, deadlines, dates, reforms. */
const LABOR_SENSITIVITY: SensitivityRules = {
  amount: { label: "金額・料率", pattern: /\d[\d,，.]*\s?(?:万円|円|%|％|‰)|保険料率|最低賃金|上限額/ },
  relief: { label: "助成金・給付・要件", pattern: /助成金|給付金|給付|要件|対象(?:と|に)なる|特例|猶予/ },
  deadline: { label: "期限・届出の時期", pattern: /期限|提出|届出|(?:\d+|[一二三四五六七八九十]+)(?:日|か月|ヶ月|カ月)以内|月\d+日まで/ },
  year: { label: "年度の書き方", pattern: /令和\d+年度?|\d{4}年度|年度版/ },
  reform: { label: "改正・施行の記述", pattern: /改正|施行|義務化|予定|見込み|新設|廃止|創設|延長/ },
};

/**
 * 社会保険労務士. The expression rules follow the 社会保険労務士法 and the
 * federation's advertising guidance: no promised subsidies or outcomes, no
 * comparison with other 社労士, and no legal work the trade may not do.
 */
export const LABOR_CONSULTANT: ProfessionPack = {
  id: "labor_consultant",
  label: "社会保険労務士",
  supervisorTitle: "社会保険労務士",
  registrationLabel: "社会保険労務士登録番号",
  reviewer: "社労士の先生",
  writingRules: [
    "依頼主の社会保険労務士事務所の立場で書く。文体は「です・ます」。一人称と表記は事務所の設定に従う。",
    "断定してよいのは、法令、厚生労働省・日本年金機構・労働局などの公的な一次資料に根拠がある場合だけ。根拠とした資料を出典として示す。",
    "保険料率・助成金の額・最低賃金・期限などの数字は年度を明記し、一次資料で確認できたものだけを書く。改正の予定は「予定」と書く。",
    "助成金は、受給を保証する表現（「必ずもらえる」「確実に受給」など）を使わず、要件と審査があることを書く。",
    "誇大な表現、根拠のない比較や最上級、他の社労士事務所との比較は使わない。",
    "個別の相談に当たる断定的な助言はしない。労使の紛争の代理など、社労士が扱えない業務を扱えるように書かない。個別の判断が必要な内容は「ご相談ください」とし、CTA につなげる。",
    "他のサイトの文章を写さない。競合の情報は構成の参考と、足りない点の把握にだけ使う。",
    "検索した人の知りたいことに冒頭で答える。必要に応じて FAQ を入れる。",
    "文字数稼ぎの水増しをしない。",
  ],
  expressionRules: [
    ...COMMON_RULES,
    { id: "labor-subsidy-guarantee", pattern: /(必ず|確実に|絶対に?)(もらえ|受給|受け取れ|採択|通り)|受給(を)?保証/g, severity: "high", category: "助成金の保証", message: "助成金の受給を保証する表現は使えません", suggestion: "「要件を満たし、審査を経て支給される場合があります」とし、要件を説明する" },
    { id: "labor-comparison", pattern: /他の(社労士|社会保険労務士|事務所)(より|と比べ|とは違|には(でき|ない))|どこよりも/g, severity: "high", category: "他の社労士との比較", message: "他の社労士事務所との比較は、広告として認められていません", suggestion: "比較をやめ、当事務所の取り扱い方を事実として説明する" },
    { id: "labor-unlicensed", pattern: /(訴訟|裁判|労働審判|交渉)(を|も|の|まで)?(代理|代行)/g, severity: "high", category: "扱えない業務", message: "社労士が扱えない業務（訴訟や交渉の代理）を扱えるように読める表現です", suggestion: "「弁護士と連携して対応します」など、扱える範囲に書き直す" },
    { id: "labor-individual", pattern: /あなたは.{0,20}(すべき|しなければなりません|もらえます|対象です)/g, severity: "medium", category: "個別の助言", message: "個別の事情によらず断定する助言になっています", suggestion: "「〜のような場合は、〜の対象になる可能性があります。詳しくはご相談ください」にする" },
    { id: "labor-future", pattern: /改正(され|になり)ます|(施行|適用)されます/g, severity: "low", category: "改正の書き方", message: "施行前の内容なら「予定」と施行日を明記する必要があります", suggestion: "「〜年〜月から施行される予定です（〇年〇月時点）」と書く" },
  ],
  trustedDomains: ["mhlw.go.jp", "nenkin.go.jp", "jsite.mhlw.go.jp", "hellowork.mhlw.go.jp", "kyoukaikenpo.or.jp", "laws.e-gov.go.jp", "elaws.e-gov.go.jp", "courts.go.jp", "nta.go.jp", "soumu.go.jp", "chusho.meti.go.jp", "jil.go.jp", "shakaihokenroumushi.jp"],
  authorityDomains: ["freee.co.jp", "biz.moneyforward.com", "smarthr.jp", "jinjer.co.jp", "obc.co.jp", "roumu.com", "nikkei.com", "ja.wikipedia.org"],
  specialtyOptions: ["就業規則", "助成金", "労働保険・社会保険の手続き", "給与計算", "労務相談・ハラスメント対応", "年金相談", "障害年金", "働き方改革・労働時間", "外国人雇用", "人事評価・賃金制度", "建設業・運送業の労務", "医療・介護の労務"],
  seasons: [
    { id: "nendo-hajime", name: "新年度の労務（4月）", start: { month: 3, day: 1 }, end: { month: 4, day: 30 }, leadWeeks: 8, terms: ["36協定", "就業規則", "入社 手続き", "社会保険 加入", "新入社員", "雇用契約書"], note: "入社と制度の切り替えが重なる時期です。改正法の施行も4月1日が多く、検索が増えます。" },
    { id: "nendo-koshin", name: "労働保険の年度更新", start: { month: 6, day: 1 }, end: { month: 7, day: 10 }, leadWeeks: 8, terms: ["労働保険", "年度更新", "労災保険", "雇用保険料"], note: "6月1日から7月10日が申告の期間です。5月に公開しておくと間に合います。" },
    { id: "santei", name: "算定基礎届", start: { month: 7, day: 1 }, end: { month: 7, day: 10 }, leadWeeks: 8, terms: ["算定基礎届", "標準報酬月額", "定時決定"], note: "7月1日から10日が提出の期間です。年度更新と重なるので、6月の前半までに公開します。" },
    { id: "saitei-chingin", name: "最低賃金の改定", start: { month: 8, day: 15 }, end: { month: 10, day: 31 }, leadWeeks: 6, terms: ["最低賃金", "賃上げ", "業務改善助成金"], note: "8月に目安が示され、10月に改定されます。賃金の見直しと助成金を調べる会社が増えます。" },
    { id: "nenmatsu-chosei", name: "年末調整・賞与の手続き", start: { month: 11, day: 1 }, end: { month: 12, day: 31 }, leadWeeks: 8, terms: ["年末調整", "賞与 社会保険料", "賞与支払届", "扶養"], note: "賞与の支払いと年末調整が重なります。給与計算の担当者の検索が増えます。" },
    { id: "josei-koubo", name: "助成金の公募と締め切り", start: { month: 1, day: 15 }, end: { month: 3, day: 31 }, leadWeeks: 8, terms: ["助成金", "キャリアアップ助成金", "両立支援", "人材開発"], note: "年度末に向けて申請の締め切りが集中し、来年度の計画も立てる時期です。" },
  ],
  officeNoun: "社会保険労務士事務所",
  officeExample: "山田社会保険労務士事務所",
  affiliationExample: "大阪府社会保険労務士会",
  sourcesPhrase: "厚生労働省や日本年金機構などの公的な資料",
  sourceExamples: "厚生労働省・日本年金機構・e-Gov 法令検索",
  keyFigures: "保険料率・助成金の額・期限",
  publishers: { "mhlw.go.jp": "厚生労働省", "nenkin.go.jp": "日本年金機構", "hellowork.mhlw.go.jp": "ハローワーク", "kyoukaikenpo.or.jp": "全国健康保険協会（協会けんぽ）", "e-gov.go.jp": "e-Gov 法令検索", "courts.go.jp": "裁判所", "nta.go.jp": "国税庁", "soumu.go.jp": "総務省", "chusho.meti.go.jp": "中小企業庁", "jil.go.jp": "労働政策研究・研修機構", "shakaihokenroumushi.jp": "全国社会保険労務士会連合会" },
  period: {
    label: fiscalLabel,
    short: (y) => `${y}年度`,
    rules: (y) => `${fiscalLabel(y)}の制度`,
    brief: (y) => `対象とする年度：${fiscalLabel(y)}（保険料率や助成金はこの年度のものを書き、年度を明記する）`,
    mustState: true,
    current: (now) => { const y = tokyoYear(now); const m = Number(new Intl.DateTimeFormat("en-US", { timeZone: "Asia/Tokyo", month: "numeric" }).format(now)); return m >= 4 ? y : y - 1; },
  },
  review: { name: "年度の見直し", when: "新しい年度（4月）や法改正の施行のあと", nowLabel: "今年度", currentArticle: "今年度の記事", updateLabel: "年度の更新", sensitivity: LABOR_SENSITIVITY },
  features: { articles: true },
};
